export class SessionResumeTranscriptError extends Error {
  constructor(){super('Saved transcript sources cannot be reconciled safely.');}
}
export type SavedTranscriptEntry={role:'ai'|'candidate';content:string;timestamp?:string|Date;model_request_id?:string;speech_act?:string;edited?:boolean;original_transcript?:string;edit_source?:string;edited_at?:string};
const fail=():never=>{throw new SessionResumeTranscriptError();};
function entries(value:unknown):SavedTranscriptEntry[]{
  if(!Array.isArray(value)||value.length>10000||JSON.stringify(value).length>2000000)return fail();
  return value.map(item=>{
    if(!item||!['ai','candidate'].includes(item.role)||typeof item.content!=='string'||!item.content.trim())return fail();
    if(item.model_request_id!==undefined&&(typeof item.model_request_id!=='string'||!/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(item.model_request_id)))return fail();
    if(item.speech_act!==undefined&&!['question','answer','acknowledgement','bridge','repeat_request','clarification','meta'].includes(item.speech_act))return fail();
    if(item.edited===true&&(typeof item.original_transcript!=='string'||(item.edit_source!==undefined&&item.edit_source!=='candidate_review')))return fail();
    return {role:item.role,content:item.content,...(item.timestamp?{timestamp:item.timestamp}:{}),
      ...(item.model_request_id?{model_request_id:item.model_request_id}:{}),...(item.speech_act?{speech_act:item.speech_act}:{}),
      ...(item.edited===true?{edited:true,original_transcript:item.original_transcript,edit_source:'candidate_review',edited_at:item.edited_at}:{})};
  });
}
// UI/context timestamps can differ slightly for the same event. They are kept
// as evidence but never used as a substitute for text, provenance or alignment.
const identity=(e:SavedTranscriptEntry)=>JSON.stringify([e.role,e.content,e.model_request_id?.toLowerCase()??null,
  e.speech_act==='question'||e.speech_act==='answer'?null:e.speech_act??null,e.edited===true,e.original_transcript??null,e.edit_source??null,e.edited_at??null]);
export function reconcileResumeTranscript(canonical:unknown,snapshot:unknown,window:unknown):SavedTranscriptEntry[]{
  const a=entries(canonical);
  if(snapshot===undefined)return a;
  const b=entries(snapshot),keysA=a.map(identity),keysB=b.map(identity);
  if(window!==undefined){
    const w=window as {version?:unknown;start?:unknown;total?:unknown};
    if(!w||w.version!==1||!Number.isSafeInteger(w.start)||!Number.isSafeInteger(w.total))return fail();
    const start=w.start as number,total=w.total as number;
    if(start<0||total<start||total>10000||total-start!==b.length||a.length<start||a.length>total)return fail();
    for(let index=start;index<a.length;index++)if(keysA[index]!==keysB[index-start])return fail();
    return [...a.slice(0,start),...b];
  }
  if(!b.length)return a.length?fail():[];
  if(!a.length)return fail(); // A legacy suffix does not prove its missing prefix.
  const matches:number[]=[];
  for(let start=0;start<a.length;start++){
    if(start+b.length<a.length)continue;
    let same=true;for(let index=start;index<a.length;index++)if(keysA[index]!==keysB[index-start]){same=false;break;}
    if(same)matches.push(start);
  }
  if(matches.length!==1)return fail();
  const merged=[...a.slice(0,matches[0]),...b];if(merged.length>10000)return fail();return merged;
}
